import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * UAE VAT configuration contracts (ERD §7.1; FRD-VAT-001 to 016, VAT-041 to 051).
 *
 * Configuration only: no determination, no VAT calculation, no posting. Every vocabulary below is
 * the approved source vocabulary, used verbatim — nothing is renamed, extended or invented. **No
 * statutory value appears here**: rates, the TRN and registration dates are Finance data
 * (FRD-VAT-056, VAT-058).
 */
export const TaxTreatmentSchema = z.enum([
    'standard',
    'zero_rated',
    'exempt',
    'out_of_scope',
    'reverse_charge',
    'blocked_non_recoverable',
    'other_recoverable',
    'capital_assets',
]);
export const RecoverabilitySchema = z.enum([
    'recoverable',
    'partially_recoverable',
    'non_recoverable',
    'not_applicable',
]);
export const TaxDirectionSchema = z.enum(['input', 'output']);
export const RegistrationStatusSchema = z.enum(['not_registered', 'registered']);
export const OperationalStateSchema = z.enum(['non_filing', 'registered_filing']);
export const ReturnCadenceSchema = z.enum(['monthly', 'quarterly']);
export const TaxMappingPurposeSchema = z.enum([
    'recoverable_input',
    'output_payable',
    'reverse_charge_input',
    'reverse_charge_output',
    'pre_registration_pending',
    'control_clearing',
    'rounding',
    'fta_settlement',
]);
const StatusSchema = z.enum(['active', 'inactive']);
const BusinessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
// ------------------------------------------------------------------- VAT registration period
export const VatRegistrationPeriodSchema = z.object({
    id: IdSchema,
    organizationId: IdSchema,
    registrationStatus: RegistrationStatusSchema,
    operationalState: OperationalStateSchema,
    trn: z.string().nullable(),
    returnCadence: ReturnCadenceSchema.nullable(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    version: z.number().int(),
});
/**
 * Opening a period. A transition is a new period, never an edit (FRD-VAT-012). The TRN and cadence
 * are supplied by Finance for a registered period; neither is defaulted.
 */
export const CreateVatRegistrationPeriodRequestSchema = z.strictObject({
    organizationId: IdSchema,
    registrationStatus: RegistrationStatusSchema,
    operationalState: OperationalStateSchema,
    trn: z.string().min(1).max(50).optional(),
    returnCadence: ReturnCadenceSchema.optional(),
    effectiveFrom: BusinessDateSchema,
});
// ------------------------------------------------------------------------------------ tax code
export const TaxCodeSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    treatment: TaxTreatmentSchema,
    recoverability: RecoverabilitySchema,
    direction: TaxDirectionSchema,
    /** The document type input VAT under this code needs attached (FRD-VAT-036); null for none. */
    supportingDocumentType: z.string().nullable().optional(),
    /** The recoverable share of a partially recoverable code (FRD-VAT-035), as a percentage. */
    recoverablePercentage: z.string().nullable().optional(),
    status: z.string(),
    version: z.number().int(),
});
export const CreateTaxCodeRequestSchema = z.strictObject({
    code: z.string().regex(/^[A-Z][A-Z0-9_]{0,29}$/, 'must be upper case, starting with a letter'),
    name: z.string().min(1).max(200),
    treatment: TaxTreatmentSchema,
    recoverability: RecoverabilitySchema,
    direction: TaxDirectionSchema,
    /** Required for, and only for, a partially recoverable code (FRD-VAT-035); no default. */
    recoverablePercentage: z
        .string()
        .regex(/^\d{1,2}(\.\d{1,2})?$/, 'a percentage between 0 and 100, up to 2 decimals')
        .optional(),
});
/**
 * A code's treatment and direction are never amended; its status and the supporting document its
 * input VAT needs (FRD-VAT-036) are Finance's configuration.
 */
export const UpdateTaxCodeRequestSchema = z.strictObject({
    status: StatusSchema,
    supportingDocumentType: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,49}$/)
        .nullable()
        .optional(),
});
// ------------------------------------------------------------------------------------ tax rate
export const TaxRateSchema = z.object({
    id: IdSchema,
    taxCodeId: IdSchema,
    taxCode: z.string(),
    /** A percentage carried as a decimal string at scale 6 (SDD §9.1). Never a number. */
    rate: z.string(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    version: z.number().int(),
});
export const CreateTaxRateRequestSchema = z.strictObject({
    taxCodeId: IdSchema,
    /**
     * The rate as a decimal string percentage, supplied by Finance and validated against the
     * authoritative UAE source before production use (FRD-VAT-058). No value is defaulted.
     */
    rate: z.string().regex(/^\d{1,3}(\.\d{1,6})?$/, 'must be a percentage with up to 6 decimals'),
    effectiveFrom: BusinessDateSchema,
    effectiveTo: BusinessDateSchema.optional(),
});
/** Ends a rate from a date (FRD-VAT-016): transactions already determined keep their rate. */
export const EndTaxRateRequestSchema = z.strictObject({ effectiveTo: BusinessDateSchema });
// --------------------------------------------------------------------------- tax account mapping
export const TaxAccountMappingSchema = z.object({
    id: IdSchema,
    mappingPurpose: TaxMappingPurposeSchema,
    taxCodeId: IdSchema.nullable(),
    taxCode: z.string().nullable(),
    registrationState: RegistrationStatusSchema.nullable(),
    accountId: IdSchema,
    accountCode: z.string(),
    status: z.string(),
    version: z.number().int(),
});
/**
 * `taxCodeId` is omitted for a purpose-level mapping such as rounding; `registrationState` is
 * omitted when the mapping applies whatever the state. No priority, condition or default exists.
 */
export const CreateTaxAccountMappingRequestSchema = z.strictObject({
    mappingPurpose: TaxMappingPurposeSchema,
    taxCodeId: IdSchema.optional(),
    registrationState: RegistrationStatusSchema.optional(),
    accountId: IdSchema,
});
export const UpdateTaxAccountMappingRequestSchema = z.strictObject({ status: StatusSchema });
// ------------------------------------------------------------------- tax determination rule
/**
 * A structured condition (SDD §14.3): a matcher, never a script. Depth is bounded at two, and the
 * operator set is closed. Validation of the tree itself happens in the shared evaluator.
 */
export const ConditionOperatorSchema = z.enum([
    'eq',
    'ne',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'not_in',
    'is_null',
    'is_not_null',
]);
const ConditionTermSchema = z.strictObject({
    attr: z.string().min(1).max(100),
    op: ConditionOperatorSchema,
    value: z.unknown().optional(),
});
const ConditionLeafGroupSchema = z.union([
    z.strictObject({ all: z.array(ConditionTermSchema).min(1).max(20) }),
    z.strictObject({ any: z.array(ConditionTermSchema).min(1).max(20) }),
]);
/** One group, optionally containing groups one level deep. Nothing nests further. */
export const ConditionSchema = z.union([
    ConditionTermSchema,
    z.strictObject({
        all: z
            .array(z.union([ConditionTermSchema, ConditionLeafGroupSchema]))
            .min(1)
            .max(20),
    }),
    z.strictObject({
        any: z
            .array(z.union([ConditionTermSchema, ConditionLeafGroupSchema]))
            .min(1)
            .max(20),
    }),
]);
export const TaxDeterminationRuleSchema = z.object({
    id: IdSchema,
    name: z.string(),
    direction: TaxDirectionSchema,
    conditions: z.unknown(),
    priority: z.number().int(),
    taxCodeId: IdSchema,
    taxCode: z.string(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    status: z.string(),
    version: z.number().int(),
});
export const CreateTaxDeterminationRuleRequestSchema = z.strictObject({
    name: z.string().min(1).max(200),
    direction: TaxDirectionSchema,
    conditions: ConditionSchema,
    /** Lowest number wins. The database refuses two active rules sharing a priority and period. */
    priority: z.number().int().min(1).max(9999),
    taxCodeId: IdSchema,
    effectiveFrom: BusinessDateSchema,
    effectiveTo: BusinessDateSchema.optional(),
});
/** A rule is withdrawn by status, or ended from a date without altering history (FRD-VAT-001). */
export const UpdateTaxDeterminationRuleRequestSchema = z.strictObject({
    status: z.enum(['active', 'inactive']),
    effectiveTo: BusinessDateSchema.nullable().optional(),
});
// ------------------------------------------------------------------------------- VAT reporting
/** A VAT report period and its filters (FRD-VAT-060, VAT-020, VAT-062). */
export const VatReportQuerySchema = z.strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    direction: TaxDirectionSchema.optional(),
    treatment: TaxTreatmentSchema.optional(),
    taxCodeId: IdSchema.optional(),
    registrationState: RegistrationStatusSchema.optional(),
    overridden: z.enum(['true']).optional(),
});
const SourceRefSchema = z
    .object({ recordType: z.string(), recordId: IdSchema, summary: z.string().nullable() })
    .nullable();
export const VatListingRowSchema = z.object({
    id: IdSchema,
    taxDate: z.string(),
    taxPoint: z.string(),
    direction: z.string(),
    taxCodeId: IdSchema,
    taxCode: z.string(),
    treatment: z.string(),
    rateApplied: z.string(),
    taxableBasis: z.string(),
    vatAmount: z.string(),
    recoverableAmount: z.string(),
    nonRecoverableAmount: z.string(),
    registrationState: z.string(),
    preRegistrationStatus: z.string().nullable(),
    counterparty: z.string().nullable(),
    source: SourceRefSchema,
    journalLineId: IdSchema.nullable(),
    adjustedDetailId: IdSchema.nullable(),
    adjustmentReference: z.string().nullable(),
    overridden: z.boolean(),
    overriddenBy: IdSchema.nullable(),
    overrideReason: z.string().nullable(),
    assessmentOutcome: z.string().nullable().optional(),
    assessmentReason: z.string().nullable().optional(),
    reclassificationJournalId: IdSchema.nullable().optional(),
});
export const VatExceptionRowSchema = z.object({
    kind: z.string(),
    message: z.string(),
    date: z.string(),
    detailId: IdSchema.nullable(),
    journalId: IdSchema.nullable(),
    journalNo: z.string().nullable(),
    accountCode: z.string().nullable(),
    amount: z.string(),
    source: SourceRefSchema,
});
export const VatReconciliationRowSchema = z.object({
    accountId: IdSchema,
    accountCode: z.string().nullable(),
    accountName: z.string().nullable(),
    purposes: z.array(z.string()),
    glMovement: z.string(),
    detailMovement: z.string(),
    difference: z.string(),
    lines: z.number().int(),
    linesWithDetail: z.number().int(),
});
export const TaxableSuppliesSchema = z.object({
    from: z.string(),
    to: z.string(),
    months: z.array(z.object({
        month: z.string(),
        basis: z.string(),
        count: z.number().int(),
        cumulative: z.string(),
    })),
    total: z.string(),
    threshold: z.string().nullable(),
    exceeded: z.boolean().nullable(),
});
export const VatPeriodReconciliationSchema = z.object({
    id: IdSchema,
    periodFrom: z.string(),
    periodTo: z.string(),
    differenceTotal: z.string(),
    nettingJournalId: IdSchema.nullable(),
    note: z.string().nullable(),
    recordedAt: z.string(),
    recordedBy: IdSchema,
    snapshot: z.array(VatReconciliationRowSchema),
});
export const RecordVatReconciliationRequestSchema = z.strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    nettingJournalId: IdSchema.optional(),
    note: z.string().trim().max(1000).optional(),
});
/** Finance's assessment of pre-registration VAT (FRD-VAT-013). */
export const AssessPreRegistrationVatRequestSchema = z.strictObject({
    outcome: z.enum(['recoverable', 'not_recoverable']),
    reason: z.string().trim().min(5).max(1000),
    reclassificationJournalId: IdSchema.optional(),
});
/** The approved transition to the registered state (FRD-VAT-012). */
export const RequestRegistrationTransitionSchema = z.strictObject({
    organizationId: IdSchema,
    effectiveFrom: BusinessDateSchema,
    trn: z.string().trim().min(1).max(50),
    returnCadence: z.enum(['monthly', 'quarterly']),
    reason: z.string().trim().max(1000).optional(),
});
export const RegistrationTransitionSchema = z.object({
    id: IdSchema,
    organizationId: IdSchema,
    effectiveFrom: z.string(),
    trn: z.string(),
    returnCadence: z.string(),
    reason: z.string().nullable(),
    status: z.string(),
    registrationPeriodId: IdSchema.nullable(),
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
    version: z.number().int(),
});
/** Activating partial exemption or the Capital Assets Scheme (FRD-VAT-054). */
export const RequestRegimeActivationSchema = z.strictObject({
    regime: z.enum(['partial_exemption', 'capital_assets_scheme']),
    effectiveFrom: BusinessDateSchema,
    accountIds: z.array(IdSchema).max(20).default([]),
    taxCodeIds: z.array(IdSchema).max(20).default([]),
    reason: z.string().trim().min(5).max(1000),
});
export const RegimeActivationSchema = z.object({
    id: IdSchema,
    regime: z.string(),
    effectiveFrom: z.string(),
    accountIds: z.array(IdSchema),
    taxCodeIds: z.array(IdSchema),
    reason: z.string(),
    status: z.string(),
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
    version: z.number().int(),
});
//# sourceMappingURL=tax.js.map